import React from 'react'
import { observer } from 'mobx-react-lite'
import { CustomTableLocalRow } from '../CustomTableLocalRow'
import TableRow from '@mui/material/TableRow'
import { CustomTableRowCell } from './CustomTableRowCell'
import { CustomTableStore, useCustomTableStore } from '../CustomTableStore'
import TableBody from '@mui/material/TableBody'
import { CustomTableCollapsibleToggle } from './CustomTableCollapsibleToggle'
import { CustomTableRowSelectionCell } from './CustomTableRowSelectionCell'
import { CustomTableRowActionCell } from './CustomTableRowActionCell'
import { CustomTableCollapsibleDetails } from './CustomTableCollapsibleDetails'
import useCustomTableRenderLog from '../hooks/useCustomTableRenderLog'
import useStore from 'reusable/mobx-store-utils'

interface CustomTableRowProps<RowData extends AnyObject, RowStore> {
   localRow: CustomTableLocalRow<RowData, RowStore>
   rowIndex: number
}

export const CustomTableRow = observer(function CustomTableRow
   <RowData extends AnyObject, RowStore>(props: CustomTableRowProps<RowData, RowStore>) {
   useCustomTableRenderLog('CustomTableRow')
   const customTableStore = useCustomTableStore<CustomTableStore<RowData, RowStore>>()

   const localStore = useStore(
      sp => ({
         onRowClick: () => {
            if (customTableStore.sp.singleRowClickSelection) {
               customTableStore.selectedRowByClick = sp.localRow.row
            }
            customTableStore.sp.onRowClick &&
               customTableStore.sp.onRowClick(sp.localRow.row, !!customTableStore.sp.singleRowClickSelection)
         },
         get rowStyle() {
            const styling = customTableStore.sp.styling || {}
            let style = {}
            if (styling.rowStyle) {
               if (typeof styling.rowStyle === 'function') {
                  style = styling.rowStyle(sp.localRow.row)
               } else {
                  style = styling.rowStyle
               }
            }
            return style
         },
         get selected() {
            return sp.localRow.row === customTableStore.selectedRowByClick || sp.localRow.selected
         },
      }),
      props
   )

   return (
      <>
         <TableBody>
            <TableRow
               hover={true}
               onClick={localStore.onRowClick}
               style={localStore.rowStyle}
               selected={localStore.selected}>
               {customTableStore.sp.selectable && (
                  <CustomTableRowSelectionCell localRow={props.localRow} />
               )}

               <CustomTableCollapsibleToggle localRow={props.localRow} />

               {customTableStore.sp.columns.map((column, index) => {
                  return (
                     <CustomTableRowCell
                        key={index}
                        column={column}
                        localRow={props.localRow}
                        rowIndex={props.rowIndex}
                     />
                  )
               })}

               <CustomTableRowActionCell localRow={props.localRow} />
            </TableRow>
         </TableBody>

         <CustomTableCollapsibleDetails localRow={props.localRow} />

         {!!customTableStore.sp.renderAdditionalRowsComponent && (
            <customTableStore.sp.renderAdditionalRowsComponent
               row={props.localRow.row}
               rowStore={props.localRow.rowStore}
            />
         )}
      </>
   )
}
)
